/* tslint:disable */
/* eslint-disable */
/**
 * Business Orchestration Layer (BOL) API
 * Business Orchestration layer (BOL) API. Public part, exposed to BOL client applications.  Glossary | Definition -- | -- <strong>Logistics Object</strong> | A resource in the BOL system that represents a business entity, such as a QuotationRequest, Quotation, or Service. Logistics Objects are managed through the BOL API and can be accessed or modified by Users based on their permissions i.e. based on their AD Entra ID roles and ACL (Access Control List) modes. <strong>Member</strong> | A data owner in the BOL system, uniquely identified by its `memberIdentifier`. Members are used to manage access control and ownership of Logistics Objects. <strong>User</strong> | A person or entity that interacts with the BOL system, typically through a client application, and beeing authenticated. Users are associated with Members and can perform actions on Logistics Objects based on their access level. User access token includes the Member identifier as part of roles claims. Each path in the API is protected by a custom role defined in AD Entra ID, and operations on Logistics Objects are protected by specific Access modes defined by members. <strong>Access Control</strong> | The mechanism by which the BOL system restricts access to API paths and resources based on the identity of the User and their associated Member. Access control is enforced through AD Entra ID roles and ACL (Access Control List) modes defined for every single Logistics Object. <strong>ACL (Access Control List)</strong> | A set of authorizations that define what actions a User can perform on a Logistics Object. The level of access granted to Users is determined by ACL modes defined in authorizations for specific Logistics Objects. ACL Modes are not directly associated with Users but rather with Members, which are the data owners of Logistics Objects. Each Logistics Object has an ACL that specifies the access modes for different Members. Members need to exist in the BOL system to be granted access to Logistics Objects. <strong>ACL Mode</strong> | The specific level of access control applied to a Logistics Object, determining what actions can be performed by Users. ACL modes are defined for each Logistics Object by `memberIdentifier` and include `READ`, `WRITE`, and `CONTROL`. Each mode includes access granted by the modes under it, so `CONTROL` includes `WRITE` and `READ`, and `WRITE` includes `READ`. <strong>Change Request</strong> | A request to modify a Logistics Object, which is processed by the BOL system. Change requests are created when a User wants to update an existing Logistics Object, and they are subject to approval and application by the system. All changes to Logistics Objects are done through change requests. This allows for a controlled and auditable process of modifications. <strong>Host</strong> | A server alias in the BOL system, used to abstract URI values internally. This allows for flexibility in the underlying infrastructure when migrating or changing server configurations. 
 *
 * The version of the OpenAPI document: 7.2.1-develop.2
 * Contact: benoit.tiers@logist-x.eu
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { mapValues } from '../runtime';
import type { PostalAddress } from './PostalAddress';
import {
    PostalAddressFromJSON,
    PostalAddressFromJSONTyped,
    PostalAddressToJSON,
    PostalAddressToJSONTyped,
} from './PostalAddress';
import type { SpecialInstruction } from './SpecialInstruction';
import {
    SpecialInstructionFromJSON,
    SpecialInstructionFromJSONTyped,
    SpecialInstructionToJSON,
    SpecialInstructionToJSONTyped,
} from './SpecialInstruction';
import type { SpecialCharacteristic } from './SpecialCharacteristic';
import {
    SpecialCharacteristicFromJSON,
    SpecialCharacteristicFromJSONTyped,
    SpecialCharacteristicToJSON,
    SpecialCharacteristicToJSONTyped,
} from './SpecialCharacteristic';
import type { ExternalReference } from './ExternalReference';
import {
    ExternalReferenceFromJSON,
    ExternalReferenceFromJSONTyped,
    ExternalReferenceToJSON,
    ExternalReferenceToJSONTyped,
} from './ExternalReference';
import type { Geolocation } from './Geolocation';
import {
    GeolocationFromJSON,
    GeolocationFromJSONTyped,
    GeolocationToJSON,
    GeolocationToJSONTyped,
} from './Geolocation';
import type { Document } from './Document';
import {
    DocumentFromJSON,
    DocumentFromJSONTyped,
    DocumentToJSON,
    DocumentToJSONTyped,
} from './Document';
import type { Contact } from './Contact';
import {
    ContactFromJSON,
    ContactFromJSONTyped,
    ContactToJSON,
    ContactToJSONTyped,
} from './Contact';

/**
 * Location details.
 * @export
 * @interface Location
 */
export interface Location {
    /**
     * Full name of the location.
     * @type {string}
     * @memberof Location
     */
    locationName?: string;
    /**
     * Location type - e.g. Airport, Freight terminal, Rail station, Seaport, etc.
     * @type {string}
     * @memberof Location
     */
    locationType?: string;
    /**
     * The location code is a UN/LOCODE city code (5 letters) or an IATA airport code (3 letters) representing an airport, freight terminal, seaport, or rail station. TODO : distinguish public and private codes. public codes in limited list whereas private code is free text 
     * @type {string}
     * @memberof Location
     */
    code?: string;
    /**
     * Address details
     * @type {PostalAddress}
     * @memberof Location
     */
    address?: PostalAddress;
    /**
     * List of contacts for the location, including any persons or teams to be contacted at the location.
     * @type {Array<Contact>}
     * @memberof Location
     */
    contacts?: Array<Contact>;
    /**
     * List of the documents used for the location, including access documents, loading/unloading lists, and administrative documents.
     * @type {Array<Document>}
     * @memberof Location
     */
    documents?: Array<Document>;
    /**
     * External references for the consignment should be used if they are defined in a legacy system that registers the location under a master data reference identifier. This is useful for searching for locations using the ID from the legacy app. Some common types of external references include 'SELLER_ID', 'BUYER_ID', 'PROVIDER_ID', 'REQUESTOR_ID', and 'IATA_ID.'
     * @type {Array<ExternalReference>}
     * @memberof Location
     */
    externalReferences?: Array<ExternalReference>;
    /**
     * Geolocation details
     * @type {Geolocation}
     * @memberof Location
     */
    geolocation?: Geolocation;
    /**
     * Special characteristics applied to the location and to be shared between partners.
     * @type {Array<SpecialCharacteristic>}
     * @memberof Location
     */
    specialCharacteristics?: Array<SpecialCharacteristic>;
    /**
     * Special instructions applied to the location and to be shared with a provider.
     * @type {Array<SpecialInstruction>}
     * @memberof Location
     */
    specialInstructions?: Array<SpecialInstruction>;
}

/**
 * Check if a given object implements the Location interface.
 */
export function instanceOfLocation(value: object): value is Location {
    return true;
}

export function LocationFromJSON(json: any): Location {
    return LocationFromJSONTyped(json, false);
}

export function LocationFromJSONTyped(json: any, ignoreDiscriminator: boolean): Location {
    if (json == null) {
        return json;
    }
    return {
        
        'locationName': json['locationName'] == null ? undefined : json['locationName'],
        'locationType': json['locationType'] == null ? undefined : json['locationType'],
        'code': json['code'] == null ? undefined : json['code'],
        'address': json['address'] == null ? undefined : PostalAddressFromJSON(json['address']),
        'contacts': json['contacts'] == null ? undefined : ((json['contacts'] as Array<any>).map(ContactFromJSON)),
        'documents': json['documents'] == null ? undefined : ((json['documents'] as Array<any>).map(DocumentFromJSON)),
        'externalReferences': json['externalReferences'] == null ? undefined : ((json['externalReferences'] as Array<any>).map(ExternalReferenceFromJSON)),
        'geolocation': json['geolocation'] == null ? undefined : GeolocationFromJSON(json['geolocation']),
        'specialCharacteristics': json['specialCharacteristics'] == null ? undefined : ((json['specialCharacteristics'] as Array<any>).map(SpecialCharacteristicFromJSON)),
        'specialInstructions': json['specialInstructions'] == null ? undefined : ((json['specialInstructions'] as Array<any>).map(SpecialInstructionFromJSON)),
    };
}

export function LocationToJSON(json: any): Location {
    return LocationToJSONTyped(json, false);
}

export function LocationToJSONTyped(value?: Location | null, ignoreDiscriminator: boolean = false): any {
    if (value == null) {
        return value;
    }

    return {
        
        'locationName': value['locationName'],
        'locationType': value['locationType'],
        'code': value['code'],
        'address': PostalAddressToJSON(value['address']),
        'contacts': value['contacts'] == null ? undefined : ((value['contacts'] as Array<any>).map(ContactToJSON)),
        'documents': value['documents'] == null ? undefined : ((value['documents'] as Array<any>).map(DocumentToJSON)),
        'externalReferences': value['externalReferences'] == null ? undefined : ((value['externalReferences'] as Array<any>).map(ExternalReferenceToJSON)),
        'geolocation': GeolocationToJSON(value['geolocation']),
        'specialCharacteristics': value['specialCharacteristics'] == null ? undefined : ((value['specialCharacteristics'] as Array<any>).map(SpecialCharacteristicToJSON)),
        'specialInstructions': value['specialInstructions'] == null ? undefined : ((value['specialInstructions'] as Array<any>).map(SpecialInstructionToJSON)),
    };
}

